import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Download, Mail, Check, ArrowRight } from 'lucide-react'
import FeedbackForm from '../chat/FeedbackForm'
import { emailSessionResults, SessionError } from '../../lib/session'
import { useAuth } from '../auth/AuthProvider'

interface SoloCompletionScreenProps {
  sessionId: string
  participantId: string
  displayName: string
  feedbackSubmitted: boolean
  onDownload: () => void
}

export default function SoloCompletionScreen({
  sessionId,
  participantId,
  displayName,
  feedbackSubmitted,
  onDownload,
}: SoloCompletionScreenProps) {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sentTo, setSentTo] = useState('')
  const [emailError, setEmailError] = useState('')
  // The summary has already gone to the account address, so the form starts
  // collapsed behind a link for sending it somewhere else as well.
  const [showEmailForm, setShowEmailForm] = useState(false)

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault()
    const address = email.trim()
    if (!address || sending) return
    setEmailError('')
    setSending(true)
    try {
      await emailSessionResults(sessionId, address, displayName)
      setSentTo(address)
    } catch (err) {
      // Every one of these carries prose meant for the reader: a bad address, the
      // three-per-session cap, a session that is not finished, email switched off.
      setEmailError(
        err instanceof SessionError && err.status >= 400 && err.status < 600
          ? err.message
          : 'Could not send that. Please try again.',
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="mx-3 mb-2 space-y-3">
      <div className="bg-navy text-stone border-2 border-navy p-6 space-y-5">
        <div>
          <p className="mono-label text-golden mb-2">Session complete</p>
          <h2 className="hl text-2xl text-stone mb-2">Nice work</h2>
          <p className="text-sm text-stone/75 max-w-md leading-relaxed">
            You picked a real workflow, wrote down the decisions behind it, and built a
            prompt you can put to work. It is all in the bundle.
          </p>
        </div>

        {/* Download the deliverables */}
        <div className="max-w-md">
          <button onClick={onDownload} className="btn btn-amber w-full">
            <Download size={16} />
            Download the bundle
          </button>
          <p className="mono-label text-stone/70 mt-2">
            System prompt, documentation, reflections, full transcript.
          </p>
        </div>

        {/* Email the summary */}
        <div className="max-w-md space-y-2 border-t border-stone/25 pt-4">
          {sentTo ? (
            <p className="text-sm text-stone/75 flex items-center gap-1.5">
              <Check size={14} className="text-golden" />
              Sent to {sentTo}.
            </p>
          ) : (
            <>
              {!showEmailForm && (
                <p className="text-sm text-stone/70">
                  Your summary went to {user?.email || 'your account address'}, and the
                  session is saved in My Sessions.{' '}
                  <button
                    onClick={() => setShowEmailForm(true)}
                    className="text-golden font-semibold underline underline-offset-2 hover:text-amber transition cursor-pointer"
                  >
                    Send it somewhere else
                  </button>
                </p>
              )}
              {showEmailForm && (
                <>
                  <form onSubmit={handleEmailSubmit} className="flex gap-2">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="flex-1 bg-white/10 border-2 border-stone/50 focus:border-stone px-3 py-2 text-sm text-stone placeholder:text-stone/70 outline-none"
                    />
                    <button
                      type="submit"
                      disabled={sending}
                      className="btn btn-on-dark shrink-0 px-4 py-2"
                    >
                      <Mail size={14} />
                      {sending ? 'Sending...' : 'Send the summary'}
                    </button>
                  </form>
                  {emailError && (
                    <p className="text-golden text-xs">{emailError}</p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Next step */}
        <div className="max-w-md border-t border-stone/25 pt-4">
          <button
            onClick={() => navigate('/?action=start&mode=team')}
            className="mono-label text-stone/70 hover:text-stone transition inline-flex items-center gap-1.5 cursor-pointer"
          >
            <Users size={14} />
            Run it with your team
            <ArrowRight size={13} />
          </button>
        </div>
      </div>

      <FeedbackForm
        sessionId={sessionId}
        participantId={participantId}
        displayName={displayName}
        hasSubmitted={feedbackSubmitted}
      />
    </div>
  )
}
